import { MutationCache, QueryCache, QueryClient, type Query } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { AppError, EmailRateLimitError, getUserMessage, isNetworkError, isNotFoundError, type ErrorCode } from "./errors";

const MESSAGE_CHARGEMENT = "Chargement impossible. Vérifiez la connexion puis réessayez.";

// Filet global : une requete en echec ne s'affiche plus comme une liste vide.
// Toast seulement sans donnees (un refetch en echec garde l'ancien affichage),
// id fixe pour qu'une panne reseau ne produise qu'un toast. `meta.toastErreur: false`
// sur les requetes dont l'ecran affiche deja l'erreur.
export function onQueryError(error: unknown, query: Query<unknown, unknown, unknown>): void {
  // Fiche introuvable : l'ecran affiche l'absence, pas de toast ni de Sentry.
  if (isNotFoundError(error)) return;
  if (!isNetworkError(error)) {
    const err = error instanceof Error ? error : new Error(getUserMessage(error));
    // RGPD-R08 : la cle complete peut contenir une recherche saisie, seul son domaine part.
    Sentry.captureException(err, { tags: { query: String(query.queryKey[0]) } });
  }
  if (query.state.data !== undefined || query.meta?.toastErreur === false) return;
  // Une erreur PostgREST brute porte un texte technique : message generique a la place.
  const message = error instanceof AppError ? error.userMessage : MESSAGE_CHARGEMENT;
  toast.error(message, { id: "erreur-chargement" });
}

// Pannes techniques d'une action : erreurs brutes de PostgREST et des Edge Functions
// (FunctionsFetchError = reseau, ignoree) et AppError techniques. Un `new Error('Veuillez ...')`
// ou une AppError validation / conflit est une consigne a l'utilisateur, pas une panne.
const ERREURS_TECHNIQUES = new Set(["PostgrestError", "FunctionsHttpError", "FunctionsRelayError"]);
const CODES_TECHNIQUES = new Set<ErrorCode>(["supabase", "td_api", "unknown"]);

function isTechnicalError(error: unknown): boolean {
  if (error instanceof EmailRateLimitError) return false;
  if (error instanceof AppError) return CODES_TECHNIQUES.has(error.code);
  return error instanceof Error && ERREURS_TECHNIQUES.has(error.name);
}

// Filet des actions : chaque mutation garde son toast, seules les pannes techniques partent
// a Sentry, sans les variables de la mutation (RGPD-R08).
export function onMutationError(error: unknown): void {
  if (isNetworkError(error) || !isTechnicalError(error)) return;
  // Edge Function : le message est toujours le meme, la Response dit laquelle et avec quel statut.
  const reponse = (error as { context?: { url?: unknown; status?: unknown } }).context;
  const tags: Record<string, string | number | boolean> = { action: true };
  if (typeof reponse?.url === "string") tags.edge_function = reponse.url.split("/").pop() ?? "";
  if (typeof reponse?.status === "number") tags.http_status = reponse.status;
  Sentry.captureException(error, { tags });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onMutationError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // postgrest-js rejoue deja 3 fois les GET en echec reseau.
      retry: 1,
    },
  },
});
